import { _ } from "../core/polyglot/polyglot.js";
import { currentTabKey } from "./forecastEditor/ForecastEditorDocumentCoordinator.js";
import { eventManager } from "@foretagsplatsen/yaem";
import { makeComparisonToolDocument } from "./comparisonTool/makeComparisonToolDocument.js";
import accountingHeaderWidget from "./shared/accountingHeaderWidget.js";
import accountingNavigationWidget from "./shared/accountingNavigationWidget.js";
import accountsPayableDocument from "./invoices/accountsPayableDocument.js";
import accountsReceivableDocument from "./invoices/accountsReceivableDocument.js";
import accountStatementDocument from "./shared/accountStatementDocument.js";
import actionsWidget from "./shared/actionsWidget.js";
import agedPayablesReportDocument from "./invoices/agedPayablesReportDocument.js";
import agedReceivablesReportDocument from "./invoices/agedReceivablesReportDocument.js";
import application from "../core/chrome/application.js";
import applicationFrame from "../core/chrome/applicationFrame.js";
import ApplicationLoadingWidget from "../core/widgets/ApplicationLoadingWidget.js";
import budgetArchiveDocument from "./budgets/budgetArchiveDocument.js";
import budgetAutomationDocument from "./budgets/automation/budgetAutomationDocument.js";
import budgetEditorDocument from "./budgets/budgetEditorDocument.js";
import businessActivityDocument from "./businessActivity/businessActivityDocument.js";
import businessTrendDocument from "./businessTrend/businessTrendDocument.js";
import chartOfAccountsEditorDocument from "./chartOfAccountsEditor/chartOfAccountsEditorDocument.js";
import companyDocument from "./company/companyDocument.js";
import companySettingsDocument from "./companySettings/companySettingsDocument.js";
import customizedReportArchiveDocument from "./reports2/customizedReportArchiveDocument.js";
import customizedReportsDocument from "./reports2/customizedReportsDocument.js";
import disclaimerDocument from "./disclaimer/disclaimerDocument.js";
import editKeyFigurePresetDocument from "./companySettings/editKeyFigurePresetDocument.js";
import foldersDocument from "./document/folders/foldersDocument.js";
import getKeyFigureService from "../core/services/getKeyFigureService.js";
import importerDocument from "./companySettings/importerDocument.js";
import integrationFactory from "../models/integration/integrationFactory.js";
import invoiceReportArchiveDocument from "./invoices/invoiceReportArchiveDocument.js";
import keyFigureDocument from "./keyFigure/single/keyFigureDocument.js";
import keyFigureEditorDocument from "./keyFigure/editor/keyFigureEditorDocument.js";
import keyFigureReportDocument from "./keyFigure/report/keyFigureReportDocument.js";
import ledgerEntryDocument from "./shared/ledgerEntryDocument.js";
import liquidityReportDocument from "./reports/liquidityReport/liquidityReportDocument.js";
import MainForecastTemplateService from "../core/services/MainForecastTemplateService.js";
import makeCompanySettingsForecastWizardDocument from "./companySettings/makeCompanySettingsForecastWizardDocument.js";
import makeForecastArchiveDocument from "./forecastArchive/makeForecastArchiveDocument.js";
import makeForecastEditorDocument from "./forecastEditor/makeForecastEditorDocument.js";
import makeForecastOnBoardingDocument from "./forecastOnBoarding/makeForecastOnBoardingDocument.js";
import makeForecastTemplateArchiveDocument from "./forecastTemplateArchive/makeForecastTemplateArchiveDocument.js";
import makeForecastViewerDocument from "./forecastViewer/makeForecastViewerDocument.js";
import makeReleaseNotesDocument from "../core/releaseNotes/makeReleaseNotesDocument.js";
import makeSearchDocument from "./search/makeSearchDocument.js";
import multipleKeyFiguresDocument from "./keyFigure/multiple/multipleKeyFiguresDocument.js";
import mySettingsDocument from "./settings/mySettingsDocument.js";
import notifier from "../core/widgets/notifier/notifier.js";
import optionsBag from "../models/options/optionsBag.js";
import overviewDocument from "./overview/overviewDocument.js";
import periodReportArchiveDocument from "./reports/periodReport/periodReportArchiveDocument.js";
import periodReportEditorDocument from "./reports/periodReport/periodReportEditorDocument.js";
import periodReportViewerDocument from "./reports/periodReport/periodReportViewerDocument.js";
import reportArchiveDocument from "./reports/reportArchiveDocument.js";
import ReportDisclaimerPopupWidget from "../core/widgets/ReportDisclaimerPopupWidget.js";
import reportEditorDocument from "./reports2/reportEditorDocument.js";
import reportViewerDocument from "./reports2/reportViewerDocument.js";
import responsive from "../core/responsive.js";
import routeNotFoundDocument from "./shared/routeNotFoundDocument.js";
import session from "../core/session.js";
import sessionTimer from "../core/sessionTimer.js";
import theme from "../core/theme.js";
import viewportWidget from "../core/chrome/viewportWidget.js";

const configuration = {
	frameOpacityTransitionTime: 600,
};

const routesForBaseFeature = [
	"business-trend",
	"business-activity",
	"company",
	"single-kpi",
	"multiple-kpis",
	"kpi-reports",
	"classic-reports",
	"new-custom-report",
	"income-statement",
	"balance-sheet",
	"cash-flow-report",
	"search-ledger",
];

/**
 * Configuration for the Accounting application.
 *
 * @param {number} [frameOpacityTransitionTime] - Indicates a
 * time in milliseconds for the duration of the frame opacity
 * transition. When the page is fully loaded, it will take it this
 * amount of time to go from full white to fully visible.
 */
export function setConfiguration({ frameOpacityTransitionTime } = {}) {
	configuration.frameOpacityTransitionTime = frameOpacityTransitionTime;
}

/**
 * `app` is the application widget for accounting. It renders a
 * `accountingNavigationWidget` as well as an `actionsWidget`.
 */
function app(spec, my) {
	spec ||= {};
	my ||= {};

	const wkHeader = spec.wkHeader;
	const setCurrentLocale = spec.setCurrentLocale;

	const that = application(spec, my);

	my.routeNotFoundDocument = routeNotFoundDocument();

	const budgetingFeature = optionsBag.fromId("budgeting");
	const forecastToBudgetFeature = optionsBag.fromId("forecast-to-budget");
	const chartOfAccountEditorFeature = optionsBag.fromId("coa-editor");
	const documentArchiveFeature = optionsBag.fromId("document-archive");
	const periodReportFeature = optionsBag.fromId("period-report");
	const customReportsFeature = optionsBag.fromId("custom-reports");
	const disclaimerFeature = optionsBag.fromId("disclaimer");
	const forecastingFeature = optionsBag.fromId("forecasting");
	const invoiceFeature = optionsBag.fromId("invoice");
	const agedInvoiceReportsFeature = optionsBag.fromId("aged-invoice-reports");
	const liquidityReportFeature = optionsBag.fromId("liquidity-report");

	let routesWithPotentialMarketingOverlay = undefined;

	function currentCompanyHasYears() {
		return session.getCompany().haveYears();
	}

	function currentCompanyHasInvoiceSupport() {
		return session.getCompany().hasInvoiceSupport();
	}

	function agencyHasBimestersEnabled() {
		return session.agency.isBimestersEnabled;
	}

	function agencyHasInvoiceReportArchiveEnabled() {
		return (
			invoiceFeature.isActive() || agedInvoiceReportsFeature.isActive()
		);
	}

	function isEarlyAccessEnabled() {
		return session.agency.isEarlyAccessEnabled;
	}

	function canViewAccountingDocuments() {
		return (
			currentCompanyHasYears() &&
			my.currentUser.canViewAccountingDocuments(session.getCompany())
		);
	}

	function canCreatePeriodReports() {
		return my.currentUser.canCreatePeriodReports(session.getCompany());
	}

	function canViewCompanySettingsDocument() {
		return my.currentUser.canViewCompanySettingsDocument(
			session.getCompany(),
		);
	}

	function canViewBudgetEditor() {
		return my.currentUser.canViewBudgetEditor(session.getCompany());
	}

	function canSaveForecastToBudget() {
		return (
			budgetingFeature.isActive() &&
			forecastToBudgetFeature.isActive() &&
			my.currentUser.canSaveForecastToBudget(session.getCompany())
		);
	}

	function canEditKeyFigures() {
		return (
			my.currentUser.canEditKeyFiguresFor(session.getCompany()) &&
			canViewAccountingDocuments()
		);
	}

	function canViewCashFlowReport() {
		return session.getCountrySettings()?.shouldShowCashFlowReport();
	}

	function canViewReleaseNotification() {
		return my.currentUser.canViewReleaseNotification();
	}

	const viewport = viewportWidget({
		cssClass: "accounting-viewport",
		canViewReleaseNotification: canViewReleaseNotification(),
		showWkHeader: wkHeader.enabled,
	});

	// Tree of navigation items
	const navigationItems = [
		{
			linkTo: "overview",
			menuId: "overview",
			label: _("Overview"),
			cssClass: "menu-overview-lg",
			roles: ["limited"],
			visible: canViewAccountingDocuments,
		},
		{
			linkTo: "business-trend",
			label: _("Business"),
			cssClass: "menu-business-activity-lg",
			roles: ["limited"],
			visible: canViewAccountingDocuments,
			children: [
				{
					linkTo: "business-trend",
					label: _("Trend"),
					cssClass: "menu-compare-lg",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
				{
					linkTo: "business-activity",
					label: _("Activity"),
					cssClass: "menu-business-activity-lg",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
			],
		},
		{
			linkTo: "company",
			label: _("The Company"),
			cssClass: "menu-company-lg",
			roles: ["limited"],
			visible: canViewAccountingDocuments,
		},
		{
			linkTo: "kpi-reports",
			label: _("KPIs"),
			cssClass: "menu-kpis-lg",
			roles: ["limited"],
			visible: canViewAccountingDocuments,
			children: [
				{
					linkTo: "single-kpi",
					label: _("KPI"),
					cssClass: "menu-single-kpi-sm",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
				{
					linkTo: "multiple-kpis",
					label: _("Compare KPIs"),
					cssClass: "menu-multiple-kpis-sm",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
				{
					linkTo: "kpi-reports",
					label: _("KPI Reports"),
					cssClass: "menu-kpi-reports-sm",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
			],
		},
		{
			linkTo: "classic-reports",
			label: _("Classic Reports"),
			cssClass: "menu-classic-sm",
			menuIds: [
				"classic-reports",
				"customized-reports",
				"new-custom-report",
				"income-statement",
				"balance-sheet",
				"budget-report",
				"cash-flow-report",
			],
			roles: ["limited"],
			visible: canViewAccountingDocuments,
			children: [
				{
					linkTo: "classic-reports",
					label: _("Report Archive"),
					cssClass: "menu-report-archive-sm",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
				{
					linkTo: "customized-reports",
					label: _("Custom Reports"),
					cssClass: "menu-report-archive-sm",
					roles: ["limited"],
					visible: my.buildRule(
						canViewAccountingDocuments,
						customReportsFeature,
					),
				},
				{
					linkTo: "income-statement",
					label: _("Income Statement"),
					cssClass: "menu-income-statement-sm",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
				{
					linkTo: "balance-sheet",
					label: _("Balance Sheet"),
					cssClass: "menu-balance-sheet-sm",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
				{
					linkTo: "budget-report",
					label: _("Budget Report"),
					cssClass: "menu-income-statement-with-budget-sm",
					roles: ["limited"],
					visible: canViewAccountingDocuments,
				},
				{
					linkTo: "cash-flow-report",
					label: _("Cash Flow"),
					cssClass: "menu-cash-flow-report-sm",
					roles: ["limited"],
					visible: my.buildRule(
						canViewAccountingDocuments,
						canViewCashFlowReport,
					),
				},
				{
					linkTo: "invoice-report-archive",
					label: _("Accounts pay/rec"),
					cssClass: "menu-report-archive-sm",
					roles: ["limited"],
					visible: my.buildRule(
						canViewAccountingDocuments,
						currentCompanyHasInvoiceSupport,
						agencyHasInvoiceReportArchiveEnabled,
					),
				},
				{
					linkTo: "liquidity-report",
					label: _("Liquidity Report"),
					cssClass: "menu-balance-sheet-sm",
					roles: ["limited"],
					visible: my.buildRule(
						canViewAccountingDocuments,
						liquidityReportFeature,
					),
				},
			],
		},
		{
			linkTo: "period-reports",
			menuIds: ["period-reports", "period-report"],
			label: _("Period Reports"),
			cssClass: "menu-period-report-archive-sm",
			roles: ["limited"],
			visible: my.buildRule(periodReportFeature),
		},
		{
			linkTo: "document-archive",
			label: _("Documents"),
			cssClass: "menu-documents-sm",
			roles: ["document-archive"],
			visible: my.buildRule(documentArchiveFeature),
		},
		{
			linkTo: "budget-archive",
			label: _("Budgeting"),
			cssClass: "menu-budget-sm",
			menuIds: ["budget-archive", "budget-editor"],
			roles: ["admin", "import", "normal"],
			visible: my.buildRule(
				canViewBudgetEditor,
				budgetingFeature,
				currentCompanyHasYears,
			),
		},
		{
			linkTo: "forecast-onboarding",
			label: _("Forecast"),
			dataTestName: "forecast-menu",
			cssClass: "menu-forecast-main-lg",
			roles: ["limited"],
			visible: my.buildRule(
				forecastingFeature,
				canViewAccountingDocuments,
			),
			children: [
				{
					click: () =>
						new MainForecastTemplateService({
							session,
							router: my.router,
						}).gotoForecastOnboardingOrMainForecastTemplateEditor(),
					label: _("Main forecast"),
					roles: ["admin"],
					dataTestName: "forecast-forecast",
					cssClass: "menu-forecast-sm",
				},
				{
					linkTo: "forecast-template-archive",
					roles: ["admin"],
					label: _("Forecast templates"),
					cssClass: "menu-forecast-templates-sm",
				},
				{
					linkTo: "forecast-archive",
					roles: ["limited"],
					label: _("Published forecasts"),
					cssClass: "menu-published-forecasts-sm",
				},
				{
					linkTo: "comparison-tool",
					roles: ["admin"],
					label: _("Comparison Tool"),
					cssClass: "menu-published-forecasts-sm",
					visible: my.buildRule(isEarlyAccessEnabled),
				},
			],
		},
		{
			linkTo: "search-ledger",
			label: _("Search the ledger"),
			cssClass: "menu-search-lg",
			visible: canViewAccountingDocuments,
			roles: ["admin", "normal"],
		},
		{
			linkTo: "settings",
			label: my.currentUser.getName() || _("User settings"),
			dataTestName: "user-settings",
			cssClass: "menu-user-lg",
			children: [
				{
					linkToUrl: session.navigationLinks.myCompanies,
					label: _("My companies"),
					cssClass: "menu-companies-sm",
					visible: () =>
						session.agency
							.isNavigationToMyCompaniesFromAccountingEnabled,
				},
				{
					linkTo: "settings",
					label: _("My settings"),
					cssClass: "menu-user-settings-sm",
				},
				...my.logoutNavigationItems(),
			],
		},
		{
			linkTo: "company-settings",
			label: _("Company Settings"),
			cssClass: "menu-settings-sm",
			menuIds: ["company-settings", "import-sie", "import-economic"],
			roles: ["admin", "import"],
			visible: canViewCompanySettingsDocument,
		},
		{
			linkTo: "release-notes",
			label: _("Release notes"),
			cssClass: "menu-reporting-lg",
			visible: canViewReleaseNotification,
			highlighted: () => viewport.shouldShowLastReleaseNotes(),
			roles: ["consultant"],
		},
		{
			linkTo: "disclaimer",
			label: _("Disclaimer"),
			cssClass: "menu-disclaimer-sm",
			visible: my.buildRule(disclaimerFeature),
		},
	];

	const navigation = accountingNavigationWidget({
		navigationItems,
		routesWithPotentialMarketingOverlay,
		visible: true,
		pdsCompliant: wkHeader.enabled,
		hideAvatar: wkHeader.enabled,
	});

	navigation.onIconModeToggled.register((isIconMode) => {
		const document = my.frame.getDocument();
		if (!document) {
			return;
		}
		document.navigationIconModeToggled(isIconMode);
	});

	const actions = actionsWidget({ showWkHeader: wkHeader.enabled });

	actions.onVisibilityChange.register((visible) => {
		const document = my.frame.getDocument();
		if (!document) {
			return;
		}
		document.actionsVisibilityChanged(visible);
	});

	const header = accountingHeaderWidget({
		visible: true,
		showWkHeader: wkHeader.enabled,
	});

	my.frame = applicationFrame({
		viewport,
		header,
		navigation,
		actions,
		wkHeader,
		setCurrentLocale,
	});

	/**
	 * Registers routes and creates menu items using addAction()
	 * Executed once on start-up to initialize the application.
	 */
	that.initialize = function () {
		sessionTimer().start();

		const loading = ApplicationLoadingWidget.instance();
		loading.setLogo(theme.agencyLogo);
		loading.show();

		if (responsive.isStandalone()) {
			responsive.setupStandaloneMode();
		}

		eventManager.at("sync").register("session-expired", () => {
			my.redirectToLogin();
		});

		my.router.setDefaultParameter("period", () =>
			session.getPeriod().toString(),
		);

		that.addAction({
			name: "kpi-editor",
			action: (doc, query) => {
				doc.show(query.keyFigureId, agencyHasBimestersEnabled());
			},
			docFactory: keyFigureEditorDocument,
			enabled: canEditKeyFigures,
		});

		that.addAction({
			name: "overview",
			action: (doc) => doc.showCurrentYear(),
			cachedDoc: true,
			docFactory: overviewDocument,
			enabled: canViewAccountingDocuments,
			roles: ["limited"],
			indexPriority: 10,
		});

		that.addAction({
			name: "business-trend",
			action: (doc) =>
				doc.show({
					isForecastingEnabled: forecastingFeature.isActive(),
				}),
			cachedDoc: true,
			docFactory: businessTrendDocument,
			enabled: canViewAccountingDocuments,
			roles: ["limited"],
			indexPriority: 10,
		});

		that.addAction({
			name: "forecast-archive",
			action: (doc, ...rest) => doc.show(...rest),
			cachedDoc: true,
			docFactory: makeForecastArchiveDocument,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				forecastingFeature,
			),
			roles: ["limited"],
		});

		that.addAction({
			name: "forecast-template-archive",
			action: (doc, ...rest) => doc.show(...rest),
			cachedDoc: true,
			docFactory: makeForecastTemplateArchiveDocument,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				forecastingFeature,
			),
			roles: ["admin"],
		});

		that.addAction({
			name: "forecast-viewer/#forecastId",
			action: (doc, forecastId, { [currentTabKey]: currentTabId }) =>
				doc.show({
					forecastId,
					currentTabId,
					canSaveToBudget: canSaveForecastToBudget(),
				}),
			cachedDoc: true,
			docFactory: makeForecastViewerDocument,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				forecastingFeature,
			),
			roles: ["limited"],
			indexPriority: 30,
		});

		that.addAction({
			name: "forecast-editor/#forecastTemplateId",
			action: (
				doc,
				forecastTemplateId,
				{ [currentTabKey]: currentTabId },
			) => doc.show({ forecastTemplateId, currentTabId }),
			cachedDoc: true,
			docFactory: makeForecastEditorDocument,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				forecastingFeature,
			),
			roles: ["admin"],
			indexPriority: 30,
		});

		that.addAction({
			name: "forecast-onboarding",
			action: (doc) =>
				doc.show({ isBimesterEnabled: agencyHasBimestersEnabled() }),
			cachedDoc: true,
			docFactory: makeForecastOnBoardingDocument,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				forecastingFeature,
			),
			roles: ["limited"],
		});

		that.addAction({
			name: "forecast-company-settings",
			action: (doc) =>
				doc.show({ isBimesterEnabled: agencyHasBimestersEnabled() }),
			cachedDoc: true,
			docFactory: makeCompanySettingsForecastWizardDocument,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				forecastingFeature,
			),
			roles: ["limited"],
		});

		that.addAction({
			name: "comparison-tool",
			action: (doc) => doc.show(),
			cachedDoc: true,
			docFactory: makeComparisonToolDocument,
			enabled: my.buildRule(
				forecastingFeature,
				canViewAccountingDocuments,
				isEarlyAccessEnabled,
			),
			roles: ["admin"],
		});

		that.addAction({
			name: "release-notes",
			action: (doc) => doc.show(),
			beforeRedirection: () => {
				viewport.markLastReleaseNotesAsSeen();
				navigation.update();
			},
			docFactory: makeReleaseNotesDocument,
			enabled: canViewReleaseNotification,
		});

		that.addAction({
			name: "business-activity",
			action: (doc) => doc.showCurrentPeriod(),
			cachedDoc: true,
			docFactory: businessActivityDocument,
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "income-statement",
			cachedDoc: true,
			action: (doc) => doc.show("income-statement"),
			docFactory: reportViewerDocument,
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "balance-sheet",
			cachedDoc: true,
			action: (doc) => doc.show("balance-sheet"),
			docFactory: reportViewerDocument,
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "budget-report",
			cachedDoc: true,
			action: (doc) => doc.show("budget-report"),
			docFactory: reportViewerDocument,
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "cash-flow-report",
			cachedDoc: true,
			action: (doc) => doc.show("cash-flow-report"),
			docFactory: reportViewerDocument,
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "liquidity-report",
			cachedDoc: true,
			action: (doc) => doc.show(),
			docFactory: liquidityReportDocument,
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				liquidityReportFeature,
			),
		});

		that.addAction({
			name: "classic-reports",
			docFactory: reportArchiveDocument,
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "customized-reports",
			docFactory: customizedReportArchiveDocument,
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				customReportsFeature,
			),
			action: (doc) => doc.show(),
		});

		that.addAction({
			name: "customized-reports/#id",
			docFactory: customizedReportsDocument,
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				customReportsFeature,
			),
			action: (doc, id) => doc.show(id),
		});

		that.addAction({
			name: "report-editor/#id",
			docFactory: reportEditorDocument,
			cachedDoc: true,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				customReportsFeature,
			),
			action: (doc, id, query) => doc.show(id, query.reset),
			roles: ["admin"],
		});

		that.addAction({
			name: "new-custom-report",
			docFactory: reportEditorDocument,
			enabled: my.buildRule(
				canViewAccountingDocuments,
				customReportsFeature,
			),
			cachedDoc: true,
			action: (doc, query) => doc.showNewTemplate(query.reset),
			roles: ["admin"],
		});

		that.addAction({
			name: "report-viewer/#id",
			docFactory: reportViewerDocument,
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				customReportsFeature,
			),
			action: (doc, id) => doc.show(id),
		});

		that.addAction({
			name: "period-reports",
			docFactory: periodReportArchiveDocument,
			roles: ["limited"],
			enabled: my.buildRule(periodReportFeature),
			action: (doc) => doc.show(),
			indexPriority: 20,
		});

		that.addAction({
			name: "period-report",
			docFactory: periodReportViewerDocument,
			roles: ["limited"],
			enabled: my.buildRule(periodReportFeature),
			action: (doc, query) => doc.show(query.id),
		});

		that.addAction({
			name: "edit-period-report",
			docFactory: periodReportEditorDocument,
			roles: ["limited"],
			enabled: my.buildRule(
				periodReportFeature.isActive(),
				canCreatePeriodReports,
				currentCompanyHasYears,
			),
			action: (doc, query) => doc.show(query.id),
		});

		that.addAction({
			name: "invoice-report-archive",
			docFactory: invoiceReportArchiveDocument,
			action: (doc) => doc.show(),
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				currentCompanyHasInvoiceSupport,
				agencyHasInvoiceReportArchiveEnabled,
			),
		});

		that.addAction({
			name: "accounts-payable-report",
			docFactory: accountsPayableDocument,
			action: (doc) => doc.show(),
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				currentCompanyHasInvoiceSupport,
				invoiceFeature,
			),
		});

		that.addAction({
			name: "accounts-receivable-report",
			docFactory: accountsReceivableDocument,
			action: (doc) => doc.show(),
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				currentCompanyHasInvoiceSupport,
				invoiceFeature,
			),
		});

		that.addAction({
			name: "single-kpi",
			docFactory: keyFigureDocument,
			action: (doc, query) => doc.show(query),
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "multiple-kpis",
			docFactory: multipleKeyFiguresDocument,
			cachedDoc: true,
			action: (doc, query) => doc.showKeyFigures(query.preset),
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "kpi-reports",
			docFactory: keyFigureReportDocument,
			cachedDoc: true,
			action: (doc, query) => doc.showKeyFigures(query),
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "search-ledger",
			action: (doc) => doc.show(),
			docFactory: makeSearchDocument,
			enabled: canViewAccountingDocuments,
			roles: ["admin", "normal"],
		});

		that.addAction({
			name: "ledger-entry",
			docFactory: ledgerEntryDocument,
			action: (doc, query) => doc.showLedgerEntry(query),
			roles: ["normal"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "account-statement",
			docFactory: accountStatementDocument,
			action: (doc, query) => doc.showAccountStatement(query),
			roles: ["normal"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "company",
			docFactory: companyDocument,
			action: (doc, query) => doc.showCurrentPeriod(query),
			roles: ["limited"],
			enabled: canViewAccountingDocuments,
		});

		that.addAction({
			name: "document-archive",
			pattern: "archive/?id",
			cachedDoc: true,
			docFactory: foldersDocument,
			action: (doc, id) => doc.setFolder(id || ""),
			roles: ["document-archive"],
			enabled: my.buildRule(documentArchiveFeature),
			indexPriority: 25,
		});

		that.addAction({
			name: "settings",
			docFactory: mySettingsDocument,
			indexPriority: 100,
		});

		that.addAction({
			name: "edit-preset",
			docFactory: editKeyFigurePresetDocument,
			action: (doc, options) => doc.editPreset(options.id),
			roles: ["admin"],
		});

		that.addAction({
			name: "company-settings",
			docFactory: companySettingsDocument,
			roles: ["admin", "import"],
			action: (doc) => doc.show(),
			enabled: canViewCompanySettingsDocument,
			indexPriority: 15,
		});

		that.addAction({
			name: "budget-archive",
			docFactory: budgetArchiveDocument,
			roles: ["admin", "import", "normal"],
			enabled: my.buildRule(canViewBudgetEditor, budgetingFeature),
			action: (doc) => doc.show(),
			indexPriority: 30,
		});

		that.addAction({
			name: "budget-editor/#budgetId",
			docFactory: budgetEditorDocument,
			roles: ["admin", "import", "normal"],
			enabled: my.buildRule(canViewBudgetEditor, budgetingFeature),
			action: (doc, budgetId) => {
				doc.show(budgetId);
			},
			indexPriority: 30,
		});

		that.addAction({
			name: "budget-automation/#budgetId",
			docFactory: budgetAutomationDocument,
			roles: ["admin", "import"],
			enabled: my.buildRule(canViewBudgetEditor, budgetingFeature),
			action: (doc, budgetId) => {
				doc.show(budgetId);
			},
			indexPriority: 30,
		});

		that.addAction({
			name: "chart-of-accounts-editor/?id",
			docFactory: chartOfAccountsEditorDocument,
			action: (doc, id) => doc.editChartOfAccounts(id),
			roles: ["admin"],
			enabled: my.buildRule(chartOfAccountEditorFeature),
		});

		that.addAction({
			name: "disclaimer",
			cachedDoc: true,
			docFactory: disclaimerDocument,
			enabled: my.buildRule(disclaimerFeature),
		});

		that.addAction({
			name: "aged-receivables-per-customer",
			docFactory: agedReceivablesReportDocument,
			action: (doc) => doc.show(),
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				currentCompanyHasInvoiceSupport,
				agedInvoiceReportsFeature,
			),
		});

		that.addAction({
			name: "aged-payables-per-supplier",
			docFactory: agedPayablesReportDocument,
			action: (doc) => doc.show(),
			roles: ["limited"],
			enabled: my.buildRule(
				canViewAccountingDocuments,
				currentCompanyHasInvoiceSupport,
				agedInvoiceReportsFeature,
			),
		});

		integrationFactory.addApplicationActionForEveryIntegration(
			that,
			importerDocument,
		);

		my.fetchAllKeyFigures().then(() => {
			optionsBag.fromId("base").whenInactive(() => {
				routesWithPotentialMarketingOverlay = [...routesForBaseFeature];

				that.enableMarketingPages({
					routesWithPotentialMarketingOverlay,
				});
			});

			my.initializeApplication();

			loading.hide();
		});
	};

	that.navigationItems = function () {
		return navigationItems;
	};

	that.renderContentOn = function (html) {
		html.render(my.frame);
		my.setupFrameTransition();

		if (disclaimerFeature.isActive()) {
			new ReportDisclaimerPopupWidget({
				company: session.getCompany(),
				agency: session.agency,
				period: session.getPeriod(),
			}).show();
		}
	};

	/**
	 * Setup an opacity transition for the frame, to get a smooth transition
	 * after the app is initialized and the loading spinner widget is
	 * hidden.
	 */
	my.setupFrameTransition = function () {
		my.frame.asJQuery().css({ opacity: 0 });

		setTimeout(() => {
			my.frame
				.asJQuery()
				.animate(
					{ opacity: 1 },
					configuration.frameOpacityTransitionTime / 2,
				);
		}, configuration.frameOpacityTransitionTime / 2);
	};

	my.handleRouting = function (options, result) {
		if (my.isPeriodRouting(result.getUrl(), my.lastUrl)) {
			my.handlePeriodRouting(options, result);
		} else {
			my.handleActionRouting(options, result);
		}

		// Store the URL as the last one for the next incoming routing match, so
		// that we can compare urls to know if it is a period change.
		my.lastUrl = result.getUrl();
	};

	my.handlePeriodRouting = function (options, result) {
		const doc = my.frame.getDocument();
		doc.periodChanged(() => my.handleActionRouting(options, result));
	};

	my.isPeriodRouting = function (currentUrl, lastUrl) {
		if (!lastUrl) {
			return false;
		}

		if (currentUrl.getPath() !== lastUrl.getPath()) {
			return false;
		}

		const lastQuery = lastUrl.getQuery();
		const currentQuery = currentUrl.getQuery();

		const parameters = new Set([
			...Object.keys(lastQuery),
			...Object.keys(currentQuery),
		]);

		return [...parameters].every(
			(param) =>
				param === "period" || lastQuery[param] === currentQuery[param],
		);
	};

	// TODO: Do a super call after moving to klassified
	my.beforeActionMatched = function (options, matchResult) {
		const periodString = matchResult.getParameters().period;
		if (periodString) {
			const company = session.getCompany();
			const period = company.findPeriodById(periodString);
			if (!period.isNullPeriod()) {
				if (period.isActive()) {
					session.setPeriod(period);
				} else {
					const companyId = company.getLegacyId();
					if (
						my.currentUser.isAdminUser(companyId) ||
						my.currentUser.isImportUser(companyId)
					) {
						notifier.warning(
							_(
								"The period {period} is not active for {company}. Redirecting to latest active month",
								{
									period: period.toString(),
									company: company.getName(),
								},
							),
						);
					} else {
						notifier.warning(
							_(
								"The period {period} does not exist for {company}. Redirecting to latest active month",
								{
									period: period.toString(),
									company: company.getName(),
								},
							),
						);
					}
					session.redirectToPeriod(company.lastActiveMonth());
				}
			}
		}
	};

	function updateHeaderCssClass() {
		Promise.resolve().then(() => {
			const headerHasWK = document
				.querySelector("#main-navbar")
				?.classList.contains("modal-wk-header");
			if (headerHasWK) {
				document.body.classList.add("has-wk-header");
			} else {
				document.body.classList.remove("has-wk-header");
			}
		});
	}

	// TODO: Do a super call after moving to klassified
	// TODO: FTGP-5444 Refactor that
	my.afterActionMatched = function (options) {
		const doc = options.currentDoc;
		if (doc.hasNavigation()) {
			navigation.activate(options.name);
		}

		doc.updatePeriodPicker();
		navigation.update();
		updateHeaderCssClass();
	};

	/**
	 * Fetch all system and custom keyFigures and fills the keyFigure cache.
	 */
	my.fetchAllKeyFigures = function () {
		return getKeyFigureService().populateCache();
	};

	return that;
}

export default app;
